/**
 * schema.org `Dataset` markup for the investment tracker.
 *
 * Why the widget emits this at all: the tracker is a public data product on a
 * page that carries no structured data of any kind, and `Dataset` is the type
 * Google runs a dedicated dataset search over. Every field below comes from
 * data the widget already has — nothing here is asserted without a source.
 *
 * What this does NOT fix: the markup is injected by JavaScript, so only
 * crawlers that render the page (Googlebot does) will ever see it. GPTBot,
 * ClaudeBot and PerplexityBot do not render, and for them the tracker stays
 * invisible. The durable fix is the same object emitted server-side into the
 * host page's HTML; when that lands it will carry the same element id and this
 * injector stands down on its own (see `injectDatasetJsonLd`).
 */

import type { MetaData } from '../types';
import { getLang, t } from '../i18n';

/** Shared with the server-rendered copy, so only one of the two is ever live. */
export const DATASET_SCRIPT_ID = 'dz-investments-dataset';

const LICENSE_URL = 'https://creativecommons.org/licenses/by-nc/4.0/';

/**
 * Build the Dataset object.
 *
 * `pageUrl` must be the canonical host-page URL — never `location.href`. The
 * widget writes filter state into the query string, and a `Dataset` that
 * claims to live at `?spotlight=halbleiter` would describe the whole dataset
 * under a filtered address. For the same reason this is built once, from
 * `/api/meta`, and never rebuilt per filter: a filtered `Dataset` is a false
 * claim about what is being described.
 */
export function buildDatasetJsonLd(meta: MetaData, pageUrl: string, apiBaseUrl: string): object {
  // Written in the page's language, which is also what inLanguage declares.
  const d = t().dataset;
  return {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: d.name,
    description: d.description,
    url: pageUrl,
    inLanguage: getLang(),
    license: LICENSE_URL,
    isAccessibleForFree: true,
    creator: {
      '@type': 'Organization',
      name: 'Dezernat Zukunft',
      url: 'https://dezernatzukunft.org',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dezernat Zukunft',
      url: 'https://dezernatzukunft.org',
    },
    spatialCoverage: {
      '@type': 'Place',
      name: d.country,
      address: { '@type': 'PostalAddress', addressCountry: 'DE' },
    },
    // Both ends come straight from /api/meta's dateRange, which is
    // MIN/MAX(announced_event_date) over the live rows.
    temporalCoverage: `${meta.dateRange.min}/${meta.dateRange.max}`,
    // The dataset's own "Letztes Update": the newest pipeline run behind a live
    // announcement, not the newest event date. Omitted rather than guessed when
    // the API build predates the field.
    ...(meta.lastUpdated ? { dateModified: meta.lastUpdated } : {}),
    variableMeasured: [...d.variables],
    // The UI's "Export" builds its CSV in the browser from this JSON endpoint
    // (see utils/csvExport.ts) — there is no CSV URL to point at, so the
    // distribution is the JSON the export is made from.
    distribution: [
      {
        '@type': 'DataDownload',
        encodingFormat: 'application/json',
        contentUrl: `${apiBaseUrl.replace(/\/$/, '')}/announcements`,
      },
    ],
  };
}

/**
 * Inject the markup into the host document's head.
 *
 * No-ops when an element with `DATASET_SCRIPT_ID` is already present: that is
 * the hand-off to a server-rendered copy, which is strictly better because a
 * non-rendering crawler can read it. Returns a cleanup that removes only a
 * script this call created.
 */
export function injectDatasetJsonLd(meta: MetaData, pageUrl: string, apiBaseUrl: string): () => void {
  if (typeof document === 'undefined') return () => {};
  if (document.getElementById(DATASET_SCRIPT_ID)) return () => {};

  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.id = DATASET_SCRIPT_ID;
  el.textContent = JSON.stringify(buildDatasetJsonLd(meta, pageUrl, apiBaseUrl));
  document.head.appendChild(el);

  return () => {
    el.remove();
  };
}

/**
 * The canonical page URL to put in the markup.
 *
 * Prefers what the host page declared (`data-page-url` on the container), then
 * its own `<link rel="canonical">`, and falls back to the current address with
 * query and hash stripped — the widget's filter params must never end up here.
 */
export function resolveCanonicalPageUrl(declared?: string | null): string {
  if (declared) return declared;
  if (typeof document !== 'undefined') {
    const canonical = document.querySelector('link[rel="canonical"]')?.getAttribute('href');
    if (canonical) return canonical;
  }
  if (typeof window === 'undefined') return '';
  return window.location.origin + window.location.pathname;
}
